import { useRef, useState } from "react"
import { ButtonPrimary } from "components/ui/buttons/ButtonPrimary/ButtonPrimary"
import { H2 } from "components/ui/typography/titles/Titles"
import { Segmented } from "components/ui/segmented/Segmented"
import { SimpleObject } from "./layout/SimpleObject/SimpleObject"
import { DualPartObject } from "./layout/DualPartObject/DualPartObject"
import {
	useCreateDualCategory,
	useCreateSimpleCategory,
	useCreateCompositeCategory,
	useObjectsCategories,
	useCreateColumnBasedCategory,
} from "hooks/api/objectsCategories/objectsCategories.hook"
import { useAddDualPartCategoryStore } from "store/dualPartCategory.store"
import { useAddSimpleCategoryStore } from "store/simpleCategory.store"
import { useCompositeCategoryStore } from "store/compositeCategory.store"
import { useMessage } from "hooks/message.hook"
import { AxiosError } from "axios"
import { ConfigurationModal } from "components/ui/modals/ConfigurationModal/ConfigurationModal"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { CompositeObject } from "./layout/CompositeObject/CompositeObject"
import { ColumnBasedObject } from "./layout/ColumnBasedObject/ColumnBasedObject"
import { createColumnBasedCategoryStore } from "store/columnBasedCategory.store"
import type { IAddObjectMethods } from "types/configuration.type"
import style from "./AddModelObject.module.scss"

const { addModelObjectButton, addModelObject, title, segmentedContent } = style

type Props = { objectCategoriesRefetch: () => void }

// Segmented options
const segmentedOptions = [
	{ label: "Simple", value: "simple" },
	{ label: "Dual part", value: "dual" },
	{ label: "Composite", value: "composite" },
	{ label: "Column-based", value: "column" },
]

export const AddModelObject = ({ objectCategoriesRefetch }: Props) => {
	const { category: simpleCategory, resetForm } = useAddSimpleCategoryStore()
	const { category: dualCategory, resetForm: resetDualForm } = useAddDualPartCategoryStore()
	const { category: compositeCategory, resetForm: resetCompositeForm } = useCompositeCategoryStore()
	const { category: columnBasedCategory, resetForm: resetColumnBasedForm } = createColumnBasedCategoryStore()

	const { data: objectCategoriesData } = useObjectsCategories()
	const { mutate: columnBasedCategoryMutation } = useCreateColumnBasedCategory()
	const { mutate: simpleCategoryMutation } = useCreateSimpleCategory()
	const { mutate: dualCategoryMutation } = useCreateDualCategory()
	const { mutate: compositeCategoryMutation } = useCreateCompositeCategory()
	const { success: successMessage } = useMessage()
	const { formatError } = useApiErrorFormatter()

	const [isModalOpen, setIsModalOpen] = useState(false)
	const [segmentedValue, setSegmentedValue] = useState<string | number>("simple")

	const simpleObjectRef = useRef<IAddObjectMethods>(null)
	const dualPartObjectRef = useRef<IAddObjectMethods>(null)
	const compositeObjectRef = useRef<IAddObjectMethods>(null)
	const columnBasedObjectRef = useRef<IAddObjectMethods>(null)

	// Handle save button click.
	// Call mutation depending on the selected option in the segmented control.
	const saveHandle = () => {
		const handleError = (e: unknown) => {
			if (e instanceof AxiosError) {
				formatError(e)
			}
		}

		const handleSuccess = (resetFormFn?: () => void, refToReset?: React.RefObject<IAddObjectMethods>) => {
			successMessage("Category created")
			setIsModalOpen(false)
			objectCategoriesRefetch()
			resetFormFn?.()
			refToReset?.current?.resetForm()
		}

		switch (segmentedValue) {
			case "simple": {
				const mutationCallbacks = {
					onSuccess: () => handleSuccess(resetForm, simpleObjectRef),
					onError: handleError,
				}
				simpleCategoryMutation(
					{
						label: simpleCategory.label,
						name: simpleCategory.name,
						superCategory: simpleCategory.superCategory,
						modelNumbers: simpleCategory.modelNumbers,
					},
					mutationCallbacks,
				)
				break
			}

			case "dual": {
				const mutationCallbacks = {
					onSuccess: () => handleSuccess(resetDualForm, dualPartObjectRef),
					onError: handleError,
				}
				dualCategoryMutation(
					{
						label: dualCategory.label,
						name: dualCategory.name,
						topLevelCategory: dualCategory.topLevelCategory,
						superCategory: dualCategory.superCategory,
						leftModelNumbers: dualCategory.leftModelNumbers,
						rightModelNumbers: dualCategory.rightModelNumbers,
						wholeModelNumbers: dualCategory.wholeModelNumbers,
					},
					mutationCallbacks,
				)
				break
			}

			case "composite": {
				const mutationCallbacks = {
					onSuccess: () => handleSuccess(resetCompositeForm, compositeObjectRef),
					onError: handleError,
				}
				compositeCategoryMutation(
					{
						label: compositeCategory.label,
						name: compositeCategory.name,
						topLevelCategory: compositeCategory.topLevelCategory,
						superCategory: compositeCategory.superCategory,
						modelNumbers: compositeCategory.modelNumbers,
						variants: compositeCategory.variants,
					},
					mutationCallbacks,
				)
				break
			}
			case "column": {
				const mutationCallbacks = {
					onSuccess: () => handleSuccess(resetColumnBasedForm, columnBasedObjectRef),
					onError: handleError,
				}

				columnBasedCategoryMutation(
					{
						label: columnBasedCategory.label,
						name: columnBasedCategory.name,
						superCategory: columnBasedCategory.superCategory,
						columns: columnBasedCategory.columns,
						modelNumbers: columnBasedCategory.modelNumbers,
					},
					mutationCallbacks,
				)
				break
			}
			default:
				console.warn(`Unknown segmented value: ${segmentedValue}`)
		}
	}

	const onChangeSegmentedHandle = (value: string | number) => {
		setSegmentedValue(value)
	}

	const onCloseModalHandle = () => {
		setIsModalOpen(false)
		resetForm()
		resetDualForm()
		resetCompositeForm()
	}

	return (
		<>
			<ButtonPrimary onClick={() => setIsModalOpen(true)} className={addModelObjectButton}>
				Add model object
			</ButtonPrimary>
			<ConfigurationModal
				submitButtonText="Save"
				isModalOpen={isModalOpen}
				setModalClose={onCloseModalHandle}
				saveHandle={saveHandle}
			>
				<div className={addModelObject}>
					<H2 className={title}>Add new object</H2>
					<Segmented options={segmentedOptions} onChange={onChangeSegmentedHandle} />
					<div className={segmentedContent}>
						{segmentedValue === "simple" && (
							<SimpleObject objectCategoriesData={objectCategoriesData} ref={simpleObjectRef} />
						)}
						{segmentedValue === "dual" && (
							<DualPartObject objectCategoriesData={objectCategoriesData} ref={dualPartObjectRef} />
						)}
						{segmentedValue === "composite" && (
							<CompositeObject objectCategoriesData={objectCategoriesData} ref={compositeObjectRef} />
						)}
						{segmentedValue === "column" && (
							<ColumnBasedObject objectCategoriesData={objectCategoriesData} ref={columnBasedObjectRef} />
						)}
					</div>
				</div>
			</ConfigurationModal>
		</>
	)
}
